import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { api, errMsg, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card, Field, Select, Btn, DataTable, FilterBar, StatusBadge } from "@/components/shared";

const DEPTS = ["Transactions", "Wallet & Funds", "KYC", "Technical", "Accounts"];
const SERVICES = ["Mobile Recharge", "DTH", "BBPS", "DMT", "AEPS", "Payout", "Fund Request", "Other"];

export function SupportList() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();
  const load = async (f = {}) => {
    setLoading(true);
    const { data } = await api.get("/tickets", { params: { ...f, status: status || undefined } });
    setRows(data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line
  return (
    <div>
      <PageHeader title="Support Tickets" subtitle="Raise and track issues with the support team.">
        <Link to="/support/new"><Btn data-testid="new-ticket-button"><Plus className="h-4 w-4" />New Ticket</Btn></Link>
      </PageHeader>
      <FilterBar onSearch={load} loading={loading}>
        <Field label="Status"><Select options={["OPEN", "IN PROGRESS", "CLOSED"]} placeholder="All" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="ticket-status-filter" /></Field>
      </FilterBar>
      <DataTable testid="tickets-table" rows={rows} onRow={(r) => nav(`/support/${r.id}`)} columns={[
        { label: "Date", render: (r) => fmtDate(r.created_at) },
        { label: "Ticket No", render: (r) => <span className="font-mono text-xs">{r.ticket_no}</span> },
        { label: "Subject", key: "subject" }, { label: "Department", key: "department" }, { label: "Service", key: "service" },
        { label: "Priority", key: "priority" }, { label: "Reference", key: "reference_id" },
        { label: "Status", render: (r) => <StatusBadge status={r.status} /> },
      ]} />
    </div>
  );
}

export function AddTicket() {
  const nav = useNavigate();
  const [f, setF] = useState({ department: DEPTS[0], priority: "Medium", ticket_type: "Query", service: SERVICES[0], reference_id: "", subject: "", message: "", attachment: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const onFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2e6) return toast.error("Max 2MB file");
    const r = new FileReader();
    r.onload = () => setF((p) => ({ ...p, attachment: r.result }));
    r.readAsDataURL(file);
  };
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/tickets", f);
      toast.success(`Ticket ${data.ticket_no} created`);
      nav(`/support/${data.id}`);
    } catch (err) { toast.error(errMsg(err)); } finally { setLoading(false); }
  };
  return (
    <div>
      <PageHeader title="New Support Ticket" />
      <Card className="max-w-3xl">
        <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2" data-testid="add-ticket-form">
          <Field label="Department"><Select options={DEPTS} value={f.department} onChange={set("department")} data-testid="ticket-dept-select" /></Field>
          <Field label="Priority"><Select options={["Low", "Medium", "High"]} value={f.priority} onChange={set("priority")} data-testid="ticket-priority-select" /></Field>
          <Field label="Ticket Type"><Select options={["Query", "Complaint", "Request"]} value={f.ticket_type} onChange={set("ticket_type")} data-testid="ticket-type-select" /></Field>
          <Field label="Service"><Select options={SERVICES} value={f.service} onChange={set("service")} data-testid="ticket-service-select" /></Field>
          <Field label="Reference / Txn ID"><input className="np-input font-mono" value={f.reference_id} onChange={set("reference_id")} data-testid="ticket-ref-input" /></Field>
          <Field label="Attachment"><input type="file" className="np-input pt-2 text-xs" onChange={onFile} data-testid="ticket-file-input" /></Field>
          <Field label="Subject" className="sm:col-span-2"><input className="np-input" value={f.subject} onChange={set("subject")} required data-testid="ticket-subject-input" /></Field>
          <Field label="Message" className="sm:col-span-2"><textarea className="np-input h-28 py-2" value={f.message} onChange={set("message")} required data-testid="ticket-message-input" /></Field>
          <Btn type="submit" loading={loading} data-testid="ticket-submit-button">Submit Ticket</Btn>
        </form>
      </Card>
    </div>
  );
}

export function TicketDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [t, setT] = useState(null);
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(false);
  useEffect(() => { api.get(`/tickets/${id}`).then(({ data }) => setT(data)); }, [id]);
  const reply = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { const { data } = await api.post(`/tickets/${id}/reply`, { message: msg }); setT(data); setMsg(""); } catch (err) { toast.error(errMsg(err)); } finally { setLoading(false); }
  };
  if (!t) return null;
  return (
    <div>
      <PageHeader title={t.subject} subtitle={`${t.ticket_no} · ${t.department} · ${t.service} · ${t.priority} priority`}><StatusBadge status={t.status} /></PageHeader>
      <Card className="max-w-3xl">
        <div className="space-y-3" data-testid="ticket-thread">
          {t.replies.map((r, i) => (
            <div key={i} className={`max-w-[85%] rounded-lg p-3 text-sm ${r.role === user.role ? "ml-auto bg-blue-50" : "bg-slate-100"}`}>
              <div className="mb-1 text-[11px] font-semibold text-slate-500">{r.by} · {fmtDate(r.at)}</div>{r.message}
            </div>
          ))}
        </div>
        {t.attachment && <a href={t.attachment} download className="mt-3 inline-block text-xs font-semibold text-blue-700" data-testid="ticket-attachment-link">Download attachment</a>}
        {t.status !== "CLOSED" && (
          <form onSubmit={reply} className="mt-4 flex gap-2" data-testid="ticket-reply-form">
            <input className="np-input" value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Write a reply…" required data-testid="ticket-reply-input" />
            <Btn type="submit" loading={loading} data-testid="ticket-reply-button">Send</Btn>
          </form>
        )}
      </Card>
    </div>
  );
}
